import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, finalize, Observable, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  AgedPackageStatusReport,
  AgedPackageStatusReportRequest,
  AgedPackageStatusRouteRequest,
  AgedPackageStatusRouteResponse,
  AgedRetailStatusReport,
  AgedRetailStatusReportRequest,
  NonBarCodeReport,
  NonBarCodeRequest,
  PackageNotFoundReport,
  PackageNotFoundRequest,
} from 'src/app/core/models/reports/reports.model';
import {
  RetailActivityReport,
  RetailActivityReportRequest,
} from 'src/app/core/models/reports/retail-activity.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class OtherReportsService {
  baseUrl = environment.apiUrl.concat('report-management/');

  constructor(
    private http: HttpClient,
    private commonUtils: CommonUtils,
    private errorHandler: ErrorhandleService,
  ) {}

  /**
   * Get Retail Activity Report for given location and date
   *
   * @param {RetailActivityReportRequest} request
   * @return {*}  {Observable<RetailActivityReport>}
   * @memberof OtherReportsService
   */
  getRetailActivityReport(
    request: RetailActivityReportRequest,
  ): Observable<RetailActivityReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<RetailActivityReport>(
        this.baseUrl.concat('retail-activity-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<RetailActivityReport>(
            error,
            error.status,
            'Report Service: Get Retail Activity Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get Aged Retail Status Report for given location Code
   *
   * @param {AgedRetailStatusReportRequest} request
   * @return {*}  {Observable<AgedRetailStatusReport>}
   * @memberof OtherReportsService
   */
  getAgedRetailStatusReport(
    request: AgedRetailStatusReportRequest,
  ): Observable<AgedRetailStatusReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<AgedRetailStatusReport>(
        this.baseUrl.concat('aged-retail-status-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<AgedRetailStatusReport>(
            error,
            error.status,
            'Other Report Service: Get Aged Retail Status Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get Non barcode Report for given location and date
   *
   * @param {RetailActivityReportRequest} request
   * @return {*}  {Observable<NonBarCodeReport>}
   * @memberof OtherReportsService
   */
  getNonBarcodeReport(
    request: NonBarCodeRequest,
  ): Observable<NonBarCodeReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<NonBarCodeReport>(
        this.baseUrl.concat('non-UPS-barcode-extract-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<NonBarCodeReport>(
            error,
            error.status,
            'Report Service: Get Non barcode Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get aged package Routes for given location
   *
   * @param {AgedPackageStatusRouteRequest} request
   * @return {*}  {Observable<AgedPackageStatusRouteResponse>}
   * @memberof OtherReportsService
   */
  getAgedPackageRoutes(
    request: AgedPackageStatusRouteRequest,
  ): Observable<AgedPackageStatusRouteResponse> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<AgedPackageStatusRouteResponse>(
        this.baseUrl.concat('aged-package-status-report/routes'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<AgedPackageStatusRouteResponse>(
            error,
            error.status,
            'Report Service: Get Aged package Routes',
            BANNER_TYPE.ROUTES_ERROR,
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get aged package Report for given location and date
   *
   * @param {AgedPackageStatusReportRequest} request
   * @return {*}  {Observable<AgedPackageStatusReport>}
   * @memberof OtherReportsService
   */
  getAgedPackageReport(
    request: AgedPackageStatusReportRequest,
  ): Observable<AgedPackageStatusReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<AgedPackageStatusReport>(
        this.baseUrl.concat('aged-package-status-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<AgedPackageStatusReport>(
            error,
            error.status,
            'Report Service: Get Aged package status Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Get aged package Report for given location and date
   *
   * @param {AgedPackageStatusReportRequest} request
   * @return {*}  {Observable<AgedPackageStatusReport>}
   * @memberof OtherReportsService
   */
  getPackageNotFoundReport(
    request: PackageNotFoundRequest,
  ): Observable<PackageNotFoundReport> {
    this.commonUtils.updateLoadingModal(true);
    return this.http
      .get<PackageNotFoundReport>(
        this.baseUrl.concat('Package-Not-Found-report'),
        {
          params: { ...request },
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this.errorHandler.handleError<PackageNotFoundReport>(
            error,
            error.status,
            'Report Service: Get package not found Report',
          ),
        ),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
